import type { ReactNode } from "react";
import { BookOpen, GraduationCap } from "lucide-react";
import type { NavBadgeTone } from "./NavBadge";

// 会員向け画面のグローバルナビの唯一の定義。
// 縦型サイドメニュー（AppSidebar / PC）・ヘッダーの副ナビ（Header / PC）・
// 横並びナビ（BottomNav / モバイル）の3箇所がここを読む。
// 片側だけ直す更新漏れを防ぐため、項目の追加・変更・削除は必ずこのファイルで行う。
//
// 大学管理画面にも NAV_ITEMS があるため、会員向けと分かる名前にしている。

/**
 * 項目を出す場所。
 *
 * - `sidebar`: PC 左の縦型サイドメニュー。主要導線の5項目だけを置く
 * - `header`: PC ヘッダー右の副ナビ。主要導線から外した機能を text リンクで置く
 * - `mobile`: モバイルのヘッダー直下の横並びナビ
 */
export type NavSurface = "sidebar" | "header" | "mobile";

/** 表示可否の判定に、フックからしか取れない情報を渡すための入れ物。 */
export interface NavVisibilityContext {
  /** 求人・応募状況の導線を出すか（高校生には出さない / #1749） */
  jobsNavVisible: boolean;
  /** 大学連携を出すか（大学の名簿に一致する学生にだけ出す / #1861） */
  universityLinkVisible: boolean;
  /** 大学へのデータ共有が有効か。大学連携の項目に「共有中」のバッジを付ける（#1861） */
  universitySharing: boolean;
}

/** 項目に付けるバッジ。ラベルと色。 */
export interface NavBadgeSpec {
  label: string;
  tone: NavBadgeTone;
}

export interface MemberNavItem {
  /** リンク先。項目を開いたときに実際に遷移するパス */
  href: string;
  /** サイドメニュー・ヘッダー副ナビでの表示名 */
  label: string;
  /** 横並びナビでの表示名。狭い画面に収めるため短くする */
  shortLabel: string;
  /**
   * 現在地判定に使う前方一致のパス。href とは別に持つ。
   * 「練習」は /practice/select へ飛ぶが、/practice 配下すべてで現在地としたいため。
   *
   * 各項目の matchPath は互いに前方一致しない形に保つ。入れ子にすると2項目が同時に強調される。
   */
  matchPath: string;
  /** 出す場所 */
  surfaces: readonly NavSurface[];
  /**
   * 表示するかどうか。
   *
   * 真偽値ではなく関数で持つ。定数にすると import 時点で process.env が読まれて値が凍結し、
   * テスト本体で vi.stubEnv してから描画する既存のフラグテストが静かに効かなくなる
   * （テストは vi.resetModules() を呼ばないためモジュールはキャッシュされ続ける）。
   */
  isEnabled: (context: NavVisibilityContext) => boolean;
  /** 新機能・ベータ版であることを示す極小バッジの文言。出す場所すべてに付く */
  badge?: string;
  /**
   * 状態で変わるバッジ。描画のたびに評価する（大学連携の「共有中」など）。
   * 静的な badge と両方あるときは、こちらが値を返した場合にそれを優先する。
   */
  dynamicBadge?: (context: NavVisibilityContext) => NavBadgeSpec | undefined;
  /**
   * アイコン。ストロークの色は呼び出し側が決める（選択中のグラデーション塗り等）。
   * アイコンを描くのは `sidebar` と `mobile` だけなので、`header` にしか出ない項目は持たない。
   */
  renderIcon?: (props: { stroke: string; className: string }) => ReactNode;
}

const alwaysEnabled = () => true;

export const MEMBER_NAV_ITEMS: MemberNavItem[] = [
  {
    href: "/dashboard",
    label: "ダッシュボード",
    shortLabel: "ダッシュボード",
    matchPath: "/dashboard",
    surfaces: ["sidebar", "mobile"],
    isEnabled: alwaysEnabled,
    renderIcon: ({ stroke, className }) => (
      <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke={stroke}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    href: "/interview/setup",
    label: "面接",
    shortLabel: "面接",
    matchPath: "/interview",
    surfaces: ["sidebar", "mobile"],
    isEnabled: alwaysEnabled,
    renderIcon: ({ stroke, className }) => (
      <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke={stroke}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    href: "/practice/select",
    label: "練習",
    shortLabel: "練習",
    matchPath: "/practice",
    surfaces: ["sidebar", "mobile"],
    isEnabled: alwaysEnabled,
    renderIcon: ({ stroke, className }) => (
      <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke={stroke}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
        <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
        <line x1="12" y1="18" x2="12" y2="22" />
      </svg>
    ),
  },
  {
    href: "/self-analysis",
    label: "自己分析",
    shortLabel: "自己分析",
    matchPath: "/self-analysis",
    surfaces: ["sidebar", "mobile"],
    isEnabled: () => process.env.NEXT_PUBLIC_FEATURE_SELF_ANALYSIS === "true",
    badge: "β",
    renderIcon: ({ stroke, className }) => (
      <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke={stroke}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        <circle cx="12" cy="10" r="2" />
      </svg>
    ),
  },
  {
    href: "/aptitude",
    label: "適性検査対策",
    // 「求人を探す→求人」「応募状況→応募」と同じく、横並びナビでは短く出す
    shortLabel: "適性",
    matchPath: "/aptitude",
    surfaces: ["sidebar", "mobile"],
    isEnabled: () => process.env.NEXT_PUBLIC_FEATURE_APTITUDE === "true",
    renderIcon: ({ stroke, className }) => (
      <BookOpen
        className={className}
        stroke={stroke}
        strokeWidth={2}
        aria-hidden="true"
      />
    ),
  },
  {
    href: "/document-review",
    label: "応募書類AI添削",
    // 「求人を探す→求人」と同じく、横並びナビでは短く出す
    shortLabel: "AI添削",
    matchPath: "/document-review",
    surfaces: ["sidebar", "mobile"],
    isEnabled: () => process.env.NEXT_PUBLIC_FEATURE_DOCUMENT_REVIEW === "true",
    renderIcon: ({ stroke, className }) => (
      <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke={stroke}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
        <path d="m9 15 2 2 4-4" />
      </svg>
    ),
  },
  {
    href: "/university-link",
    label: "大学連携",
    shortLabel: "大学",
    matchPath: "/university-link",
    surfaces: ["sidebar", "mobile"],
    // 大学から招待された（名簿に一致する）学生にだけ出す。判定はフックが持ち、
    // 取得前・取得失敗のときは出さない（#1861）
    isEnabled: ({ universityLinkVisible }) => universityLinkVisible,
    // 大学へのデータ共有が有効な間は緑の「共有中」を付け、共有の状態を開かずに分かるようにする
    dynamicBadge: ({ universitySharing }) =>
      universitySharing ? { label: "共有中", tone: "success" } : undefined,
    renderIcon: ({ stroke, className }) => (
      <GraduationCap
        className={className}
        stroke={stroke}
        strokeWidth={2}
        aria-hidden="true"
      />
    ),
  },

  // ここから下は主要導線から外し、PC ではヘッダー右の副ナビへ置く項目。
  // 求人・応募状況はモバイルでの到達性を保つため横並びナビにも残す。
  {
    href: "/jobs",
    label: "求人を探す",
    shortLabel: "求人",
    matchPath: "/jobs",
    surfaces: ["header", "mobile"],
    isEnabled: ({ jobsNavVisible }) => jobsNavVisible,
    badge: "NEW",
    renderIcon: ({ stroke, className }) => (
      <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke={stroke}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      </svg>
    ),
  },
  {
    href: "/applications",
    label: "応募状況",
    shortLabel: "応募",
    matchPath: "/applications",
    surfaces: ["header", "mobile"],
    isEnabled: ({ jobsNavVisible }) => jobsNavVisible,
    renderIcon: ({ stroke, className }) => (
      <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke={stroke}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <polyline points="9 14 11 16 15 12" />
      </svg>
    ),
  },
  {
    href: "/scripts",
    label: "スクリプト",
    shortLabel: "スクリプト",
    matchPath: "/scripts",
    // 移行前も横並びナビには無かった。モバイルはダッシュボード等の画面内導線から到達する
    surfaces: ["header"],
    isEnabled: () => process.env.NEXT_PUBLIC_FEATURE_SCRIPT === "true",
  },
];

/** その場所に出す項目のうち、いま表示すべきものだけを返す。描画のたびに呼ぶ。 */
export function getVisibleMemberNavItems(
  surface: NavSurface,
  context: NavVisibilityContext,
): MemberNavItem[] {
  return MEMBER_NAV_ITEMS.filter(
    (item) => item.surfaces.includes(surface) && item.isEnabled(context),
  );
}

/** 項目に付けるバッジを解決する。状態で変わるものを優先し、無ければ静的な文言（accent 色）。 */
export function getMemberNavBadge(
  item: MemberNavItem,
  context: NavVisibilityContext,
): NavBadgeSpec | undefined {
  const dynamic = item.dynamicBadge?.(context);
  if (dynamic) return dynamic;
  return item.badge ? { label: item.badge, tone: "accent" } : undefined;
}

/** 現在地かどうか。パスの前方一致で判定する。 */
export function isMemberNavItemActive(
  item: MemberNavItem,
  pathname: string | null,
): boolean {
  return !!pathname?.startsWith(item.matchPath);
}
